import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import {
  listErpDocuments,
  validateErpDocuments,
  executeErpDocuments,
  rejectErpDocument,
  assignErpDocumentWarehouse,
} from "@/infrastructure/erp-document/erpDocumentService";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import type { ErpDocument, ErpValidationResult, ErpExecutionReportItem, ErpExecutionPick, WarehouseRecord } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

type ModalPhase = "list" | "report";
type SemaphoreStatus = "PENDING" | "GREEN" | "RED";

interface Props {
  type: "sale" | "purchase" | "adjustment";
  draftId: string;
  draftReference: string;
  onClose: () => void;
  onExecuted: () => void;
  embedded?: boolean;
}

const PAGE_SIZE = 15;

export function ErpDocumentModal({ type, draftId, draftReference, onClose, onExecuted, embedded = false }: Props) {
  const { t } = useI18n();
  const [phase, setPhase] = useState<ModalPhase>("list");
  const [allDocuments, setAllDocuments] = useState<ErpDocument[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [semaphores, setSemaphores] = useState<Record<string, SemaphoreStatus>>({});
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({});
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [report, setReport] = useState<ErpExecutionReportItem[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);

  // Load all documents once
  useEffect(() => {
    setLoading(true);
    listErpDocuments({ status: "PENDING", type, limit: 500 })
      .then(res => setAllDocuments(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  // Load warehouses
  useEffect(() => { listWarehouses().then(setWarehouses).catch(() => {}); }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  // Client-side filter
  const filtered = allDocuments.filter(doc => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return doc.documentNumber.toLowerCase().includes(q) || (doc.customerCode ?? "").toLowerCase().includes(q) || (doc.customerName ?? "").toLowerCase().includes(q);
  });

  // Client-side pagination
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginatedDocs = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [search]);

  const toggleSelect = (id: string) => {
    const doc = allDocuments.find(d => d.id === id);
    if (doc && !doc.warehouseId) return; // Can't select docs without warehouse
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectableDocs = paginatedDocs.filter(d => !!d.warehouseId);
    const allPageSelected = selectableDocs.every(d => selected.has(d.id));
    if (allPageSelected) {
      setSelected(prev => { const next = new Set(prev); selectableDocs.forEach(d => next.delete(d.id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); selectableDocs.forEach(d => next.add(d.id)); return next; });
    }
  };

  const handleAssignWarehouse = async (docId: string, warehouseId: string) => {
    try {
      await assignErpDocumentWarehouse(docId, warehouseId);
      setAllDocuments(prev => prev.map(d => d.id === docId ? { ...d, warehouseId } : d));
    } catch {
      showToast("Error al asignar bodega");
    }
  };

  const getWarehouseName = (warehouseId: string) => {
    return warehouses.find(w => w.warehouse_id === warehouseId)?.warehouse_name ?? warehouseId.slice(0, 8);
  };

  const handleValidate = async () => {
    if (selected.size === 0) return;
    setValidating(true);
    try {
      const res = await validateErpDocuments([...selected]);
      const map: Record<string, SemaphoreStatus> = { ...semaphores };
      const msgs: Record<string, string> = { ...validationMessages };
      res.results.forEach(r => { map[r.documentId] = r.status; msgs[r.documentId] = r.message; });
      setSemaphores(map);
      setValidationMessages(msgs);
      setValidated(true);
    } catch {}
    setValidating(false);
  };

  const handleExecute = async () => {
    const greenIds = [...selected].filter(id => semaphores[id] === "GREEN");
    if (greenIds.length === 0) return;
    setExecuting(true);
    try {
      const body: any = { documentIds: greenIds };
      if (type === "sale") body.dispatchId = draftId;
      else if (type === "purchase") body.receiptId = draftId;
      else body.adjustmentId = draftId;

      const res = await executeErpDocuments(body);
      setReport(res.report);
      setPhase("report");
      onExecuted();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "";
      showToast(translateExecuteError(msg, t));
    }
    setExecuting(false);
  };

  const handleReject = async (id: string) => {
    try {
      await rejectErpDocument(id);
      setAllDocuments(prev => prev.filter(d => d.id !== id));
      setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch {}
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 5000); };

  const greenCount = [...selected].filter(id => semaphores[id] === "GREEN").length;
  const pendingCount = allDocuments.filter(d => !semaphores[d.id] || semaphores[d.id] === "PENDING").length;
  const validCount = allDocuments.filter(d => semaphores[d.id] === "GREEN").length;
  const errorCount = allDocuments.filter(d => semaphores[d.id] === "RED").length;

  const handleDownloadExcel = () => {
    const rows: string[][] = [["Documento", "Producto", "Cantidad", "Ubicación"]];
    report.forEach(r => {
      r.picks.forEach(p => { rows.push([r.documentNumber, p.productCode, String(p.quantity), p.location]); });
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `reporte-picks-${draftReference}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  if (embedded) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
        {/* Info bar */}
        <div style={{ padding: "12px 24px", display: "flex", alignItems: "center", gap: "16px", borderBottom: "1px solid #f7f7f7" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: colors.info, fontWeight: typography.weight.semibold }}>
            {t.erpDocuments?.selected ?? "Seleccionados"}: {selected.size} de {allDocuments.length}
          </span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.warning, background: "rgba(245,158,11,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>
            {pendingCount} {t.erpDocuments?.statusPending ?? "Pendiente"}
          </span>
          {validCount > 0 && <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.success, background: "rgba(34,197,94,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>{validCount} {t.erpDocuments?.statusValid ?? "Válido"}</span>}
          {errorCount > 0 && <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.error, background: "rgba(239,68,68,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>{errorCount} {t.erpDocuments?.statusError ?? "Error"}</span>}
        </div>
        {/* Search */}
        <div style={{ padding: "12px 24px 0" }}>
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t.erpDocuments?.searchPlaceholder ?? "Buscar por # documento o cliente..."} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", width: "100%", boxSizing: "border-box" }} />
        </div>
        {/* Table */}
        <div style={{ flex: 1, overflow: "auto", padding: "12px 24px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>
          ) : allDocuments.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px", fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.erpDocuments?.empty ?? "No hay documentos pendientes"}</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  <th style={thStyle}><input type="checkbox" checked={paginatedDocs.filter(d => !!d.warehouseId).length > 0 && paginatedDocs.filter(d => !!d.warehouseId).every(d => selected.has(d.id))} onChange={toggleSelectAll} style={{ accentColor: "#111", width: "15px", height: "15px", cursor: "pointer" }} /></th>
                  <th style={thStyle}>{t.erpDocuments?.colDocument ?? "# Documento"}</th>
                  <th style={thStyle}>{t.erpDocuments?.colDate ?? "Fecha"}</th>
                  <th style={thStyle}>{t.erpDocuments?.colCustomer ?? "Cliente"}</th>
                  <th style={thStyle}>{t.erpDocuments?.colAmount ?? "Monto"}</th>
                  <th style={thStyle}>Bodega</th>
                  <th style={thStyle}>{t.erpDocuments?.colStatus ?? "Novedad"}</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedDocs.map(doc => {
                  const sem = semaphores[doc.id] ?? "PENDING";
                  const hasWarehouse = !!doc.warehouseId;
                  const rowBg = sem === "GREEN" ? "rgba(34,197,94,0.05)" : sem === "RED" ? "rgba(239,68,68,0.05)" : !hasWarehouse ? "rgba(245,158,11,0.04)" : "transparent";
                  return (
                    <tr key={doc.id} style={{ borderBottom: "1px solid #f7f7f7", background: rowBg }}>
                      <td style={tdStyle}><input type="checkbox" checked={selected.has(doc.id)} onChange={() => toggleSelect(doc.id)} disabled={!hasWarehouse} title={!hasWarehouse ? "Asigne una bodega primero" : undefined} style={{ accentColor: "#111", width: "15px", height: "15px", cursor: hasWarehouse ? "pointer" : "not-allowed", opacity: hasWarehouse ? 1 : 0.4 }} /></td>
                      <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{doc.documentNumber}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{doc.documentDate?.slice(0, 10)}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{doc.customerName ?? doc.customerCode}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>${doc.documentTotal.toLocaleString("es-CO")}</span></td>
                      <td style={tdStyle}>
                        {hasWarehouse ? (
                          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#555" }}>{getWarehouseName(doc.warehouseId!)}</span>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.warning} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                            <select defaultValue="" onChange={e => { if (e.target.value) handleAssignWarehouse(doc.id, e.target.value); }} style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "5px", padding: "4px 8px", cursor: "pointer", maxWidth: "130px" }}>
                              <option value="" disabled>Seleccione</option>
                              {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
                            </select>
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}><SemaphoreBadge status={sem} message={validationMessages[doc.id]} /></td>
                      <td style={tdStyle}>
                        <button onClick={() => handleReject(doc.id)} title={t.erpDocuments?.reject ?? "Rechazar"} style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", display: "flex", padding: "4px", borderRadius: "4px", transition: `color ${transitions.fast}` }} onMouseEnter={e => (e.currentTarget.style.color = colors.error)} onMouseLeave={e => (e.currentTarget.style.color = "#ccc")}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: "8px 24px", borderTop: "1px solid #f7f7f7", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={pgBtnStyle(false)}>‹</button>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={pgBtnStyle(false)}>›</button>
          </div>
        )}
        {/* Footer actions */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #f2f2f2", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={handleValidate} disabled={selected.size === 0 || validating} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: selected.size === 0 || validating ? "#ccc" : colors.info, border: "none", borderRadius: "9px", padding: "10px 20px", cursor: selected.size === 0 || validating ? "not-allowed" : "pointer", transition: `background ${transitions.fast}` }}>
            {validating ? "..." : `✓ ${t.erpDocuments?.validate ?? "Validar Inventario"}`}
          </button>
          <button onClick={handleExecute} disabled={!validated || greenCount === 0 || executing} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: !validated || greenCount === 0 || executing ? "#ccc" : colors.success, border: "none", borderRadius: "9px", padding: "10px 20px", cursor: !validated || greenCount === 0 || executing ? "not-allowed" : "pointer", transition: `background ${transitions.fast}` }}>
            {executing ? "..." : `▶ ${t.erpDocuments?.execute ?? "Ejecutar Importación"}`}
          </button>
        </div>
        {toast && <div style={{ position: "absolute", bottom: "80px", left: "50%", transform: "translateX(-50%)", background: colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "10px 18px", borderRadius: "8px", zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>{toast}</div>}
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "900px", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #f2f2f2", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
              {t.erpDocuments?.title ?? "Gestión de Documentos ERP"}
            </h2>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{draftReference}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: "4px" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {phase === "list" ? (
          <>
            {/* Info bar */}
            <div style={{ padding: "12px 28px", display: "flex", alignItems: "center", gap: "16px", borderBottom: "1px solid #f7f7f7" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: colors.info, fontWeight: typography.weight.semibold }}>
                {t.erpDocuments?.selected ?? "Seleccionados"}: {selected.size} de {allDocuments.length}
              </span>
              <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.warning, background: "rgba(245,158,11,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>
                {pendingCount} {t.erpDocuments?.statusPending ?? "Pendiente"}
              </span>
              <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.success, background: "rgba(34,197,94,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>
                {validCount} {t.erpDocuments?.statusValid ?? "Válido"}
              </span>
              {errorCount > 0 && <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: colors.error, background: "rgba(239,68,68,0.1)", padding: "3px 10px", borderRadius: "9999px" }}>
                {errorCount} {t.erpDocuments?.statusError ?? "Error"}
              </span>}
            </div>

            {/* Search */}
            <div style={{ padding: "12px 28px 0" }}>
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.erpDocuments?.searchPlaceholder ?? "Buscar por # documento o cliente..."}
                style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", width: "100%", boxSizing: "border-box" }}
              />
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflow: "auto", padding: "12px 28px" }}>
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                  <span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                </div>
              ) : allDocuments.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px", fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>
                  {t.erpDocuments?.empty ?? "No hay documentos pendientes"}
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                      <th style={thStyle}><input type="checkbox" checked={paginatedDocs.filter(d => !!d.warehouseId).length > 0 && paginatedDocs.filter(d => !!d.warehouseId).every(d => selected.has(d.id))} onChange={toggleSelectAll} style={{ accentColor: "#111", width: "15px", height: "15px", cursor: "pointer" }} /></th>
                      <th style={thStyle}>{t.erpDocuments?.colDocument ?? "# Documento"}</th>
                      <th style={thStyle}>{t.erpDocuments?.colDate ?? "Fecha"}</th>
                      <th style={thStyle}>{t.erpDocuments?.colCustomer ?? "Cliente"}</th>
                      <th style={thStyle}>{t.erpDocuments?.colAmount ?? "Monto"}</th>
                      <th style={thStyle}>Bodega</th>
                      <th style={thStyle}>{t.erpDocuments?.colStatus ?? "Novedad"}</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDocs.map(doc => {
                      const sem = semaphores[doc.id] ?? "PENDING";
                      const rowBg = sem === "GREEN" ? "rgba(34,197,94,0.05)" : sem === "RED" ? "rgba(239,68,68,0.05)" : !doc.warehouseId ? "rgba(245,158,11,0.04)" : "transparent";
                      const hasWarehouse = !!doc.warehouseId;
                      return (
                        <tr key={doc.id} style={{ borderBottom: "1px solid #f7f7f7", background: rowBg, transition: `background ${transitions.fast}` }}>
                          <td style={tdStyle}>
                            <input
                              type="checkbox"
                              checked={selected.has(doc.id)}
                              onChange={() => toggleSelect(doc.id)}
                              disabled={!hasWarehouse}
                              title={!hasWarehouse ? "Asigne una bodega primero" : undefined}
                              style={{ accentColor: "#111", width: "15px", height: "15px", cursor: hasWarehouse ? "pointer" : "not-allowed", opacity: hasWarehouse ? 1 : 0.4 }}
                            />
                          </td>
                          <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{doc.documentNumber}</span></td>
                          <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{doc.documentDate?.slice(0, 10)}</span></td>
                          <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{doc.customerName ?? doc.customerCode}</span></td>
                          <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>${doc.documentTotal.toLocaleString("es-CO")}</span></td>
                          <td style={tdStyle}>
                            {hasWarehouse ? (
                              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#555" }}>{getWarehouseName(doc.warehouseId!)}</span>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.warning} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                <select
                                  defaultValue=""
                                  onChange={e => { if (e.target.value) handleAssignWarehouse(doc.id, e.target.value); }}
                                  style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "5px", padding: "4px 8px", cursor: "pointer", maxWidth: "140px" }}
                                >
                                  <option value="" disabled>Seleccione bodega</option>
                                  {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
                                </select>
                              </div>
                            )}
                          </td>
                          <td style={tdStyle}><SemaphoreBadge status={sem} message={validationMessages[doc.id]} /></td>
                          <td style={tdStyle}>
                            <button onClick={() => handleReject(doc.id)} title={t.erpDocuments?.reject ?? "Rechazar"} style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", display: "flex", padding: "4px", borderRadius: "4px", transition: `color ${transitions.fast}` }} onMouseEnter={e => (e.currentTarget.style.color = colors.error)} onMouseLeave={e => (e.currentTarget.style.color = "#ccc")}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: "8px 28px", borderTop: "1px solid #f7f7f7", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={pgBtnStyle(false)}>‹</button>
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{page} / {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={pgBtnStyle(false)}>›</button>
              </div>
            )}

            {/* Footer */}
            <div style={{ padding: "16px 28px", borderTop: "1px solid #f2f2f2", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>
                {t.erpDocuments?.cancel ?? "Cancelar"}
              </button>
              <button
                onClick={handleValidate}
                disabled={selected.size === 0 || validating}
                style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: selected.size === 0 || validating ? "#ccc" : colors.info, border: "none", borderRadius: "9px", padding: "10px 20px", cursor: selected.size === 0 || validating ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }}
              >
                {validating ? "..." : `✓ ${t.erpDocuments?.validate ?? "Validar Inventario"}`}
              </button>
              <button
                onClick={handleExecute}
                disabled={!validated || greenCount === 0 || executing}
                style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: !validated || greenCount === 0 || executing ? "#ccc" : colors.success, border: "none", borderRadius: "9px", padding: "10px 20px", cursor: !validated || greenCount === 0 || executing ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }}
              >
                {executing ? "..." : `▶ ${t.erpDocuments?.execute ?? "Ejecutar Importación"}`}
              </button>
            </div>
          </>
        ) : (
          /* REPORT PHASE */
          <>
            <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
              <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: "0 0 16px" }}>
                {t.erpDocuments?.reportTitle ?? "Reporte de Ubicaciones"}
              </h3>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                    <th style={thStyle}>{t.erpDocuments?.colDocument ?? "Documento"}</th>
                    <th style={thStyle}>{t.erpDocuments?.colProduct ?? "Producto"}</th>
                    <th style={thStyle}>{t.erpDocuments?.colQuantity ?? "Cantidad"}</th>
                    <th style={thStyle}>{t.erpDocuments?.colLocation ?? "Ubicación"}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.flatMap(r =>
                    r.picks.map((pick, i) => (
                      <tr key={`${r.documentId}-${i}`} style={{ borderBottom: "1px solid #f7f7f7" }}>
                        <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{r.documentNumber}</span></td>
                        <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{pick.productCode}</span></td>
                        <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", fontWeight: typography.weight.semibold }}>{pick.quantity}</span></td>
                        <td style={tdStyle}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555", fontWeight: typography.weight.semibold }}>{pick.location}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "16px 28px", borderTop: "1px solid #f2f2f2", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>
                {t.erpDocuments?.close ?? "Cerrar"}
              </button>
              <button onClick={handleDownloadExcel} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")} onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                {t.erpDocuments?.download ?? "Descargar"}
              </button>
            </div>
          </>
        )}

        {toast && <div style={{ position: "absolute", bottom: "80px", left: "50%", transform: "translateX(-50%)", background: colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "10px 18px", borderRadius: "8px", zIndex: 10 }}>{toast}</div>}
      </div>
    </div>
  );
}

function SemaphoreBadge({ status, message }: { status: SemaphoreStatus; message?: string }) {
  const { t } = useI18n();
  const [showDetail, setShowDetail] = useState(false);
  const config: Record<SemaphoreStatus, { bg: string; color: string; label: string }> = {
    PENDING: { bg: "rgba(156,163,175,0.15)", color: "#6b7280", label: t.erpDocuments?.statusPending ?? "Pendiente" },
    GREEN: { bg: "rgba(34,197,94,0.1)", color: "#16a34a", label: t.erpDocuments?.statusValid ?? "Válido" },
    RED: { bg: "rgba(239,68,68,0.1)", color: "#dc2626", label: t.erpDocuments?.statusError ?? "Error" },
  };
  const cfg = config[status];

  const translatedMessage = message ? translateValidationMessage(message, t) : null;
  const hasDetail = !!translatedMessage && status !== "PENDING";

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <span
        onClick={() => { if (hasDetail) setShowDetail(v => !v); }}
        style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: cfg.bg, color: cfg.color, fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "9999px", cursor: hasDetail ? "pointer" : "default" }}
      >
        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: cfg.color }} />
        {cfg.label}
      </span>
      {showDetail && translatedMessage && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "8px 12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 50, minWidth: "200px", maxWidth: "300px" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#555", lineHeight: "1.4" }}>{translatedMessage}</span>
        </div>
      )}
    </div>
  );
}

function translateValidationMessage(message: string, t: any): string {
  if (message === "Document not found") return t.erpDocuments?.msgDocNotFound ?? "Documento no encontrado";
  if (message === "No warehouse assigned") return t.erpDocuments?.msgNoWarehouse ?? "Sin bodega asignada";
  if (message === "Stock available") return t.erpDocuments?.msgStockOk ?? "Stock disponible";
  if (message === "Purchase - no stock validation required") return t.erpDocuments?.msgPurchaseOk ?? "Compra — sin validación de stock requerida";
  // Insufficient stock pattern
  const match = message.match(/Insufficient stock for (.+) \(available: (\d+), required: (\d+)\)/);
  if (match) {
    const template = t.erpDocuments?.msgInsufficientStock ?? "Stock insuficiente para {product} (disponible: {available}, requerido: {required})";
    return template.replace("{product}", match[1]).replace("{available}", match[2]).replace("{required}", match[3]);
  }
  return message;
}

function translateExecuteError(message: string, t: any): string {
  if (message === "No active location found for warehouse") return t.erpDocuments?.errNoLocation ?? "La bodega no tiene ubicaciones activas. Cree una sección antes de ejecutar.";
  if (message.startsWith("Product not found:")) {
    const code = message.replace("Product not found: ", "");
    return (t.erpDocuments?.errProductNotFound ?? "Producto no encontrado: {code}").replace("{code}", code);
  }
  if (message.includes("Insufficient stock for") && message.includes("during execution")) {
    const match = message.match(/Insufficient stock for (.+) during execution/);
    if (match) return (t.erpDocuments?.errInsufficientStockExec ?? "Stock insuficiente para {product} durante la ejecución.").replace("{product}", match[1]);
  }
  if (message) return message;
  return t.erpDocuments?.errorExecute ?? "Error al ejecutar la importación.";
}

const thStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "12px 12px", textAlign: "left" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", verticalAlign: "middle" };
function pgBtnStyle(active: boolean): React.CSSProperties {
  return { width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #e8e8e8", background: active ? "#111" : "#fff", color: active ? "#fff" : "#666", fontFamily: typography.fontFamily, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
}
